import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { DatePicker } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import styled from 'styled-components';

import ErrorMessage from './ErrorMessage';
import { normalizePortalText } from 'utils/baochauTheme';
import useFormControl, { mergeDescribedBy } from './useFormControl';
import { getFormPopupContainer } from './popupHelpers';
import './style.scss';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;
const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

const normalizePlaceholder = (placeholder) => {
  if (Array.isArray(placeholder)) {
    return placeholder.map((item) => (typeof item === 'string' ? normalizePortalText(item) : item));
  }

  return typeof placeholder === 'string' ? normalizePortalText(placeholder) : placeholder;
};

const parseDateValue = (value, format) => {
  if (!value) return null;
  if (dayjs.isDayjs(value)) return value.isValid() ? value : null;

  const formats = Array.isArray(format) ? format : [format || DEFAULT_DATE_FORMAT];
  for (const item of formats.filter(Boolean)) {
    const parsed = dayjs(value, item, true);
    if (parsed.isValid()) return parsed;
  }

  const fallback = dayjs(value);
  return fallback.isValid() ? fallback : null;
};

const FormStyled = styled.div`
  .ant-picker {
    width: 100%;
    min-height: var(--bw-control-height);
    padding: 0 var(--bw-control-padding-x);
    border-radius: var(--bw-control-radius);
    border: 1px solid var(--bw-control-border);
    background: #ffffff;
    box-shadow: none;
  }

  .ant-picker-input > input {
    color: #101828 !important;
    font-size: 14px;
  }

  .ant-picker-input > input::placeholder {
    color: #98a2b3;
    opacity: 1;
  }

  .ant-picker-range-separator,
  .ant-picker-suffix,
  .ant-picker-clear {
    color: #667085;
  }

  .ant-picker.is-invalid {
    border-color: #fda29b;
    box-shadow: 0 0 0 3px rgba(240, 68, 56, 0.1);
  }

  .ant-picker-focused,
  .ant-picker:hover {
    border-color: var(--bw-control-focus);
    box-shadow: 0 0 0 3px rgba(237, 50, 55, 0.1);
  }
`;

const FormRangePicker = ({
  fieldStart,
  fieldEnd,
  validation,
  placeholder,
  disabled = false,
  style,
  format,
  bordered = false,
  allowClear,
  minDate,
  readOnly = false,
  getPopupContainer = getFormPopupContainer,
  id,
  className,
  'aria-describedby': ariaDescribedBy,
  ...props
}) => {
  const startControl = useFormControl({
    field: fieldStart,
    validation,
    id,
    ariaDescribedBy,
  });
  const endControl = useFormControl({
    field: fieldEnd,
    validation,
    ariaDescribedBy,
  });
  const fieldStartError = startControl.error;
  const fieldEndError = endControl.error;

  const error = useMemo(() => {
    return fieldStartError ?? fieldEndError;
  }, [fieldStartError, fieldEndError]);

  // const disabledDate = (current) => {
  //   // Can't select days before today
  //   return current && current < moment().endOf('day');
  // };
  const pickerFormat = format || DEFAULT_DATE_FORMAT;
  const toDayjsOrNull = (val) => parseDateValue(val, pickerFormat);
  const startValue = startControl.fieldValue;
  const endValue = endControl.fieldValue;
  const errorId = fieldStartError ? startControl.errorId : endControl.errorId;
  const describedBy = mergeDescribedBy(
    ariaDescribedBy,
    fieldStartError ? startControl.errorId : undefined,
    fieldEndError ? endControl.errorId : undefined,
  );
  return (
    <FormStyled>
      <RangePicker
        id={startControl.inputId}
        className={['bw_form_control', className, error ? 'is-invalid' : '', readOnly ? 'is-readonly' : '']
          .filter(Boolean)
          .join(' ')}
        allowClear={allowClear}
        disabled={disabled || readOnly}
        aria-invalid={Boolean(error) || undefined}
        aria-describedby={describedBy}
        getPopupContainer={getPopupContainer}
        minDate={minDate ? dayjs(minDate) : dayjs(new Date(1999, 10 - 1, 25))}
        placeholder={normalizePlaceholder(placeholder)}
        value={startValue || endValue ? [toDayjsOrNull(startValue), toDayjsOrNull(endValue)] : null}
        onChange={(dates, dateStrings) => {
          if (dates) {
            startControl.setFieldValue(dateStrings?.[0] || null);
            endControl.setFieldValue(dateStrings?.[1] || null);
          } else {
            startControl.setFieldValue(null);
            endControl.setFieldValue(null);
          }
        }}
        format={pickerFormat}
        style={{
          width: '100%',
          ...style,
        }}
        {...props}
      />
      {error && <ErrorMessage id={errorId} message={error?.message} />}
    </FormStyled>
  );
};

FormRangePicker.propTypes = {
  fieldStart: PropTypes.string,
  fieldEnd: PropTypes.string,
  validation: PropTypes.object,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  getPopupContainer: PropTypes.func,
  id: PropTypes.string,
  format: PropTypes.string,
  allowClear: PropTypes.bool,
  bordered: PropTypes.bool,
};

FormRangePicker.defaultProps = {};

export default FormRangePicker;
